import React from 'react';

/**
 * Full-page error fallback shown when the top-level ErrorBoundary
 * catches an uncaught render error.
 *
 * Goals:
 * - Avoid the dreaded "blank white screen" so the user always has a
 *   way out (refresh, go home, contact support).
 * - Keep the design consistent with the rest of the app (DM Sans, the
 *   primary color #057CE4).
 * - Show a readable error message in dev; a generic one in production
 *   (we don't want to leak internals to end-users).
 *
 * Props:
 * - error: the Error instance caught by the boundary (or null).
 * - onRetry: () => void — resets the boundary state and re-renders
 *   the children. If not provided, falls back to a full page reload.
 */
const STYLE_ID = 'app-fullpage-error-style';

const injectKeyframes = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const tag = document.createElement('style');
  tag.id = STYLE_ID;
  tag.textContent = `
    @keyframes app-error-pulse {
      0%   { transform: scale(1);    opacity: 1; }
      50%  { transform: scale(1.04); opacity: 0.85; }
      100% { transform: scale(1);    opacity: 1; }
    }
    .app-fullpage-error {
      position: fixed;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #f8fafc;
      z-index: 10000;
      padding: 24px;
      text-align: center;
      font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
      color: #0f172a;
    }
    .app-fullpage-error__icon {
      width: 96px;
      height: 96px;
      border-radius: 50%;
      background: #fef3c7;
      color: #b45309;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 48px;
      font-weight: 700;
      margin-bottom: 24px;
      animation: app-error-pulse 2.4s ease-in-out infinite;
    }
    .app-fullpage-error__title {
      font-size: 22px;
      font-weight: 600;
      margin: 0 0 8px;
    }
    .app-fullpage-error__message {
      font-size: 14px;
      color: #64748b;
      max-width: 480px;
      margin: 0 0 24px;
      line-height: 1.5;
    }
    .app-fullpage-error__actions {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      justify-content: center;
    }
    .app-fullpage-error__btn {
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      border: 1px solid transparent;
      transition: background 0.15s ease, transform 0.05s ease;
    }
    .app-fullpage-error__btn:active { transform: translateY(1px); }
    .app-fullpage-error__btn--primary {
      background: #057CE4;
      color: #fff;
    }
    .app-fullpage-error__btn--primary:hover { background: #0466c2; }
    .app-fullpage-error__btn--secondary {
      background: #fff;
      color: #0f172a;
      border-color: #cbd5e1;
    }
    .app-fullpage-error__btn--secondary:hover { background: #f1f5f9; }
    .app-fullpage-error__detail {
      margin-top: 24px;
      padding: 12px 16px;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      color: #475569;
      max-width: 480px;
      text-align: left;
      white-space: pre-wrap;
      word-break: break-word;
    }
  `;
  document.head.appendChild(tag);
};

const isDev = process.env.NODE_ENV !== 'production';

const FullPageErrorScreen = ({ error, onRetry }) => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const handleRetry = () => {
    if (typeof onRetry === 'function') {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="app-fullpage-error" role="alert">
      <div className="app-fullpage-error__icon" aria-hidden="true">!</div>
      <h1 className="app-fullpage-error__title">Terjadi kesalahan pada aplikasi</h1>
      <p className="app-fullpage-error__message">
        Mohon maaf, aplikasi mengalami error yang tidak terduga.
        Tim kami sudah otomatis menerima laporan. Silakan coba lagi,
        atau kembali ke halaman utama.
      </p>
      <div className="app-fullpage-error__actions">
        <button
          type="button"
          className="app-fullpage-error__btn app-fullpage-error__btn--primary"
          onClick={handleRetry}
        >
          Coba lagi
        </button>
        <button
          type="button"
          className="app-fullpage-error__btn app-fullpage-error__btn--secondary"
          onClick={handleHome}
        >
          Kembali ke Beranda
        </button>
      </div>
      {isDev && error && (
        <pre className="app-fullpage-error__detail">
          {error.name}: {error.message}
          {'\n\n'}
          {error.stack}
        </pre>
      )}
    </div>
  );
};

export default FullPageErrorScreen;
